import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { db, StoreConfig, AdminCategory, AdminProduct } from '../services/backend';

// Declare global types for Google Merchant Widget
declare global {
  interface Window {
    merchantWidgetScript: HTMLScriptElement;
    merchantwidget: any;
  }
}

const StoreLayout: React.FC = () => {
  const [config, setConfig] = useState<StoreConfig | null>(null);
  const [headerTheme, setHeaderTheme] = useState<'white' | 'dark' | 'auto'>('auto');
  const [filteredCategories, setFilteredCategories] = useState<AdminCategory[]>([]);

  useEffect(() => {
    const loadData = async () => {
      // Parallel fetch for config, products, and categories
      const [configData, products, categories] = await Promise.all([
        db.getStoreConfig(),
        db.getProducts(),
        db.getCategories(),
      ]);

      setConfig(configData);

      if (configData?.colors) {
        const root = document.documentElement;
        root.style.setProperty('--color-alphamed-brown', configData.colors.primary);
        root.style.setProperty('--color-alphamed-gold', configData.colors.secondary);
        root.style.setProperty('--color-alphamed-cream', configData.colors.accent);
      }

      // Filter Categories Logic
      const activeProductCategoryIds = new Set(
        products
          .filter((p) => p.status === 'active' && p.isAvailable !== false)
          .map((p) => p.categoryId)
      );

      // Recursive function to check if a category or its children have active products
      const hasActiveProducts = (categoryId: string): boolean => {
        // Direct products
        if (activeProductCategoryIds.has(categoryId)) return true;

        // Check children
        const children = categories.filter((c) => c.parentId === categoryId);
        return children.some((child) => hasActiveProducts(child.id));
      };

      const visibleCategories = categories.filter((cat) => {
        // Must be active itself
        if (cat.isActive === false) return false;

        // Must have active products or active children
        return hasActiveProducts(cat.id);
      });

      setFilteredCategories(visibleCategories);
    };

    loadData();
  }, []);

  // Google Customer Reviews Badge
  useEffect(() => {
    const MERCHANT_ID = 5716686829;
    const SCRIPT_ID = 'merchant-widget-script';

    if (document.getElementById(SCRIPT_ID)) {
      return;
    }

    // Define global handler
    window.merchantWidgetScript = document.createElement('script');
    window.merchantWidgetScript.id = SCRIPT_ID;
    window.merchantWidgetScript.src = 'https://www.gstatic.com/shopping/merchant/merchantwidget.js';
    window.merchantWidgetScript.async = true;
    window.merchantWidgetScript.defer = true;

    window.merchantWidgetScript.onload = () => {
      if (window.merchantwidget) {
        window.merchantwidget.start({
          merchant_id: MERCHANT_ID,
          position: 'BOTTOM_RIGHT',
        });
      }
    };

    document.body.appendChild(window.merchantWidgetScript);

    return () => {
      // Optional cleanup
      // We don't remove the script to avoid re-initialization issues in dev mode
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <Header config={config?.topbar} theme={headerTheme} categories={filteredCategories} />

      <main className="flex-grow">
        <Outlet context={{ config, setHeaderTheme, categories: filteredCategories }} />
      </main>

      <Footer config={config?.banners?.footer} categories={filteredCategories} />
    </div>
  );
};

export default StoreLayout;
